// frontend/src/hooks/useAreas.ts
// React Query hooks for areas service operations

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  areasService, 
  ServiceArea, 
  AreasFilters, 
  CreateAreaData, 
  UpdateAreaData, 
  AreasResponse,
  PincodeValidationResponse,
  PincodeSuggestionsResponse,
  AreaCoverage
} from '@/lib/api/areasService';
import { tokenManager } from '@/lib/httpClient';

// Query keys for areas
export const areaKeys = {
  all: ['areas'] as const,
  lists: () => [...areaKeys.all, 'list'] as const,
  list: (filters: AreasFilters) => [...areaKeys.lists(), filters] as const,
  details: () => [...areaKeys.all, 'detail'] as const,
  detail: (id: number) => [...areaKeys.details(), id] as const,
  coverage: () => [...areaKeys.all, 'coverage'] as const,
  validation: () => [...areaKeys.all, 'validation'] as const,
  suggestions: () => [...areaKeys.all, 'suggestions'] as const,
} as const;

// Get areas with filters (role-based: admin sees all, provider sees own)
export const useAreas = (filters: AreasFilters = {}) => {
  return useQuery({
    queryKey: areaKeys.list(filters),
    queryFn: async () => {
      const response = await areasService.getAreas(filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get specific area by ID
export const useArea = (id: number) => {
  return useQuery({
    queryKey: areaKeys.detail(id),
    queryFn: async () => {
      const response = await areasService.getAreaById(id);
      if (response.success && response.data) {
        return response.data; // Data is directly in response.data, not nested under .area
      }
      throw new Error(response.message);
    },
    enabled: !!id && !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403 || error?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Create new area (Provider only)
export const useCreateArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAreaData) => {
      const response = await areasService.createArea(data);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (newArea) => {
      // Invalidate areas lists
      queryClient.invalidateQueries({ queryKey: areaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: areaKeys.coverage() });
      
      // Add new area to cache if we have the data
      if (newArea?.area_id) {
        queryClient.setQueryData(
          areaKeys.detail(newArea.area_id),
          newArea
        );
      }
    },
  });
};

// Update area (Provider only)
export const useUpdateArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateAreaData }) => {
      const response = await areasService.updateArea(id, data);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (updatedArea, variables) => {
      // Update specific area in cache
      if (updatedArea?.area_id) {
        queryClient.setQueryData(
          areaKeys.detail(variables.id),
          updatedArea
        );
      }
      
      // Invalidate lists and coverage
      queryClient.invalidateQueries({ queryKey: areaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: areaKeys.coverage() });
    },
  });
};

// Delete area (Provider only)
export const useDeleteArea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await areasService.deleteArea(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: areaKeys.detail(variables) });
      
      // Invalidate lists and coverage
      queryClient.invalidateQueries({ queryKey: areaKeys.lists() });
      queryClient.invalidateQueries({ queryKey: areaKeys.coverage() });
    },
  });
};

// Toggle area status (Provider only)
export const useToggleAreaStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await areasService.toggleAreaStatus(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (updatedArea, variables) => {
      // Update specific area in cache
      if (updatedArea?.area) {
        queryClient.setQueryData(
          areaKeys.detail(variables),
          updatedArea.area
        );
      }
      
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: areaKeys.lists() });
    },
  });
};


// Validate pincode
export const useValidatePincode = () => {
  return useMutation({
    mutationFn: async (pincode: string) => {
      const response = await areasService.validatePincode(pincode);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
  });
};

// Get pincode suggestions
export const usePincodeSuggestions = () => {
  return useMutation({
    mutationFn: async (query: string) => {
      const response = await areasService.getPincodeSuggestions(query);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
  });
};

// Get area coverage analysis
export const useAreaCoverage = (filters: AreasFilters = {}) => {
  return useQuery({
    queryKey: [...areaKeys.coverage(), filters],
    queryFn: async () => {
      const response = await areasService.getAreaCoverage(filters);
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get provider's service areas (for provider dashboard)
export const useProviderServiceAreas = () => {
  return useQuery({
    queryKey: [...areaKeys.lists(), 'provider'],
    queryFn: async () => {
      const response = await areasService.getAreas({ provider_only: true });
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Search areas by location
export const useSearchAreas = (searchQuery: string) => {
  return useQuery({
    queryKey: [...areaKeys.lists(), 'search', searchQuery],
    queryFn: async () => {
      const response = await areasService.getAreas({ 
        search: searchQuery,
        limit: 20 
      });
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!searchQuery && searchQuery.length > 2 && !!tokenManager.getToken(),
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get areas by state/city for filtering
export const useAreasByLocation = (state?: string, city?: string) => {
  return useQuery({
    queryKey: [...areaKeys.lists(), 'location', { state, city }],
    queryFn: async () => {
      const response = await areasService.getAreas({ 
        state, 
        city,
        status: 'active' 
      });
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error(response.message);
    },
    enabled: !!(state || city) && !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Check area availability for booking
export const useCheckAreaAvailability = () => {
  return useMutation({
    mutationFn: async ({ pincode, serviceType }: { pincode: string; serviceType?: string }) => {
      const response = await areasService.checkAreaAvailability(pincode, serviceType);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
  });
};